import { AppRoutes } from "@/config/app.routes"
import { handleApiError } from '@/utils/apiError'
import { useAuth } from "@/providers/AuthProvider"
import { AxiosError, AxiosInstance, isAxiosError } from "axios"
import { useEffect, useRef } from "react"
import { NavigationService, useNavigation } from "@/providers/NavigationProvider"
import AppService from '@/services/app.service'
import { AUTH, USER } from "@/config/api.endpoints"
import { ApiError, ExtendedApiError } from "@/types/api.types"

NavigationService.setBaseUrl(AppService.getAppBaseURL() || "")

export const useAxiosInterceptors = (client: AxiosInstance) => {
  const { logout, refreshSession } = useAuth()
  const { navigate } = useNavigation()
  const isRefreshing = useRef(false)
  const refreshPromise = useRef<Promise<void> | null>(null)

  useEffect(() => {
    const interceptorId = client.interceptors.response.use(
      response => {
        if (response.data?.redirectTo) {
          NavigationService.navigate(response.data.redirectTo)
        }
        return response
      },
      async (error: ExtendedApiError) => {
        if (error.response?.data?.redirectTo) {
          NavigationService.navigate(error.response.data.redirectTo)
        }

        const status: number | undefined = error.response?.status
        const originalRequest = error.config

        if (!isRefreshing.current) {
          switch (status) {
            case 401:
              handleApiError(error, { customMessage: 'Your session has expired. Please log in again.', toastType: 'warning' })
              await logout()
              throw error
            case 403:
              if (error.response?.data) throw error.response.data
              handleApiError(error, { customMessage: 'Access denied. You don\'t have permission for this action.' })
              //navigate(AppRoutes.FORBIDDEN)
              navigate(AppRoutes.ERROR)
              throw error
            case 503:
              handleApiError(error, { customMessage: 'Server error occurred. Please try again later.' })
              // navigate(AppRoutes.MAINTENANCE)
              navigate(AppRoutes.ERROR)
              throw error
            default:
              if (status && status >= 500) {
                handleApiError(error, { customMessage: 'Server error occurred. Please try again later.' })
              } else if (!error.response) {
                handleApiError(error, {
                  customMessage: error.code === 'ECONNABORTED'
                    ? 'Request timeout. Please check your connection.'
                    : 'Network error. Please check your internet connection.',
                  toastType: error.code === 'ECONNABORTED' ? 'warning' : 'error'
                })
              }
          }        
        }

        if (status !== 401 ||
          originalRequest === undefined ||
          originalRequest.url?.includes(AUTH.REFRESH) ||
          originalRequest.url?.includes(USER.ME)) {
          throw error
        }

        if (isRefreshing.current && refreshPromise.current) {
          try {
            await refreshPromise.current
            return client.request(originalRequest)
          } catch {
            throw error
          }
        }

        // if (
        //   originalRequest &&
        //   !originalRequest._retry
        // ) {
        //   originalRequest._retry = true

        //   try {
        //     console.log("Refreshing access token")
        //     await refreshSession()
        //     return client(originalRequest)
        //   } catch (_error) {
        //    await client.post(AUTH.LOGOUT)
        //     if (isAxiosError(_error)) {
        //       const axiosError = _error as AxiosError<ApiError>
        //       throw new Error(axiosError.response?.data.message ?? "Unknown error")
        //     }
        //     throw _error
        //   }
        // }

        isRefreshing.current = true
        refreshPromise.current = (async () => {
          try {
            await refreshSession()
          } catch (refreshError) {
            handleApiError(refreshError as ApiError, { 
              customMessage: 'Session could not be renewed. Please log in again.',
              toastType: 'warning',
             })
            await logout()
            throw refreshError
          } finally {
            isRefreshing.current = false
            refreshPromise.current = null
          }
        })()

        try {
          await refreshPromise.current
          return client.request(originalRequest)
        } catch {
          throw error
        }
      }
    )

    return () => client.interceptors.response.eject(interceptorId)
  }, [client, navigate, logout, refreshSession])

}
